import React from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import {
  isUndefined,
  isArray,
  isString,
  bindAll,
  isEqual,
  isEmpty,
  difference,
  find,
} from 'lodash-es'
import { intl } from 'lang/intl'
import { ModalComponent, VideoSelector } from '..'

import './VideoSelectorModalComponent.scss'

const getDefaultSelectionType = (value) => {
  // It can be "String", "Array of Strings" or "Array of Objects"
  if (isString(value)) {
    return 'string'
  }

  if (isArray(value) && isString(value[0])) {
    return 'array-string'
  }

  return 'array-object'
}

function getObjectKey(object) {
  if (object.object_id) {
    return 'object_id'
  }

  if (object.photo_id) {
    return 'photo_id'
  }

  if (object.live_id) {
    return 'live_id'
  }

  if (object.album_id) {
    return 'album_id'
  }

  return undefined
}

function toIdList(objects) {
  return objects.map((video) => video[getObjectKey(video)])
}

function getDiff(oldValue, newValue) {
  const oldVideoIds = toIdList(oldValue)
  const newVideoIds = toIdList(newValue)

  return {
    added: difference(newVideoIds, oldVideoIds),
    removed: difference(oldVideoIds, newVideoIds),
  }
}

class VideoSelectorModalComponent extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      selectedVideos: props.value,
      selectionType: getDefaultSelectionType(props.value),
    }

    this.originalValue = props.value

    bindAll(this, 'onCancel', 'onSubmit', 'onVideoSelectionChange')
  }

  UNSAFE_componentWillReceiveProps(props) {
    const newValue = isUndefined(props.value) ? '' : props.value

    this.originalValue = props.value

    const { value } = this.props
    if (isEqual(value, props.value)) {
      return
    }

    const { selectedVideos } = this.state
    if (!isEqual(newValue, selectedVideos)) {
      this.setState({ selectedVideos: props.value })
    }
  }

  onCancel() {
    this.closeModal()
  }

  onSubmit() {
    const { selectionType, selectedVideos } = this.state
    const { onChange } = this.props
    if (selectionType === 'array-object') {
      const { added, removed } = getDiff(this.originalValue, selectedVideos)

      onChange(selectedVideos, added, removed)
    } else {
      onChange(selectedVideos)
    }

    this.closeModal()
  }

  onVideoSelectionChange(valueString, valueArrayObject, valueArrayString) {
    const { selectionType } = this.state
    if (selectionType === 'string') {
      this.setState({ selectedVideos: valueString })
      return
    }

    if (selectionType === 'array-string') {
      this.setState({ selectedVideos: valueArrayString })
      return
    }

    this.setValueArrayObjectState(valueArrayObject)
  }

  setValueArrayObjectState(newValue) {
    const { selectedVideos } = this.state
    const { added, removed } = getDiff(selectedVideos, newValue)
    const addedVideoIds = new Set(added)
    const deletedVideoIds = new Set(removed)

    this.setState((state) => {
      const result = state.selectedVideos.filter(
        (video) => !deletedVideoIds.has(video[getObjectKey(video)])
      )

      addedVideoIds.forEach((videoId) => {
        const video = find(
          newValue,
          (vid) => vid[getObjectKey(vid)] === videoId
        )
        result.push(video)
      })

      return { selectedVideos: result }
    })
  }

  getSelection() {
    const { selectionType, selectedVideos } = this.state
    if (selectionType === 'string') {
      return selectedVideos
    }

    if (selectionType === 'array-string') {
      return selectedVideos.join(' ')
    }

    return selectedVideos
      .map((video) => {
        let objectType
        const keyProp = getObjectKey(video)

        if (!isEmpty(video.photo_id)) {
          objectType = 'photo'
        } else if (!isEmpty(video.live_id)) {
          objectType = 'live'
        } else if (!isEmpty(video.album_id)) {
          objectType = 'album'
        }

        return `${objectType}:${video[keyProp]}`
      })
      .join(' ')
  }

  closeModal() {
    const { onCloseModal } = this.props
    // NOTE (kasperkronborg): This is a hack for resetting the value on close. An empty array was needed,
    // if you are not satified with this, then read this component code and come up with a better solution.
    const { isResettingStateOnClose } = this.props
    if (isResettingStateOnClose) {
      this.setState({ selectedVideos: [] })
    }
    onCloseModal()
  }

  render() {
    const { isModalOpen, modalProps, fixedHeight, selectorProps } = this.props
    return (
      <ModalComponent
        key={isModalOpen.toString()}
        wrapClassName="video-selector-modal-component"
        visible={isModalOpen}
        onCancel={this.onCancel}
        onOk={this.onSubmit}
        width={1080}
        showFooter
        noPadding
        okText={intl.formatMessage({ defaultMessage: 'Select' })}
        {...modalProps}
      >
        <div className={classnames({ 'fixed-height': fixedHeight })}>
          <VideoSelector
            selection={this.getSelection()}
            onChange={this.onVideoSelectionChange}
            expanded
            {...selectorProps}
          />
        </div>
      </ModalComponent>
    )
  }
}

VideoSelectorModalComponent.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.object),
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]),
  onChange: PropTypes.func,
  isModalOpen: PropTypes.bool,
  onCloseModal: PropTypes.func,
  selectorProps: PropTypes.object,
  modalProps: PropTypes.object,
  fixedHeight: PropTypes.bool,
  isResettingStateOnClose: PropTypes.bool,
}

VideoSelectorModalComponent.defaultProps = {
  selectorProps: {},
  value: '',
  modalProps: {},
  isModalOpen: false,
  onCloseModal: () => {},
  fixedHeight: false,
  isResettingStateOnClose: false,
}

export default VideoSelectorModalComponent
